import DashLayout from "@/layouts/DashLayout";
import RoleProtectedPage from "@/layouts/RoleProtectedPage";
import PageTitle from "@/components/PageTitle";
import { useAppSelector } from "@/redux/features/hooks";
import { useRestaurantCurrency } from "@/lib/hooks";
import { useGetOpenSessionQuery } from "@/redux/services/pos/registerSession.api";
import { Roles } from "@/redux/services/user/user.type";
import { usePermissions } from "@/hooks/usePermissions";
import OpenSessionForm from "@/components/pos/register/OpenSessionForm";
import ActiveSession from "@/components/pos/register/ActiveSession";
import QuickMovementActions from "@/components/pos/register/QuickMovementActions";
import SessionLedgerTable from "@/components/pos/register/SessionLedgerTable";
import Spinner from "@/components/Spinner";
import { Link } from "react-router-dom";
import { History } from "lucide-react";

const REGISTER_SESSION_POLL_MS = 30_000;

const RegisterPage = () => {
  const restaurant = useAppSelector((state) => state.user.currentRestaurant);
  const { can } = usePermissions();
  const canOpenRegister = can("cash_register.open");
  const currency = useRestaurantCurrency();

  // Mercure (`cash_register_session:updated`) is the fast path for the
  // expected cash; polling and a refetch on every visit keep the figure right
  // when the live stream does not deliver on this device (SFRESTO-207).
  const { data: openSession, isLoading } = useGetOpenSessionQuery(
    { restaurantId: restaurant?.id ?? "" },
    {
      skip: !restaurant?.id,
      pollingInterval: REGISTER_SESSION_POLL_MS,
      refetchOnMountOrArgChange: true,
    }
  );

  return (
    <DashLayout>
      <RoleProtectedPage
        allowedRoles={[Roles.manager, Roles.cashier]}
        allowedPermissions={["cash_register.view"]}
      >
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <PageTitle title="Caisse" description="Gestion de la caisse enregistreuse" />
            {/* Same gate as /register/sessions (cash_register.view). */}
            {can("cash_register.view") && (
              <Link
                to="/register/sessions"
                className="flex items-center gap-2 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
              >
                <History className="h-4 w-4" />
                Historique
              </Link>
            )}
          </div>

          {!restaurant || isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Spinner />
            </div>
          ) : openSession ? (
            <>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
                <ActiveSession session={openSession} currency={currency} />
                <QuickMovementActions
                  restaurantId={restaurant.id}
                  sessionUuid={openSession.id}
                  currency={currency}
                />
              </div>
              <SessionLedgerTable
                sessionId={openSession.id}
                currency={currency}
              />
            </>
          ) : canOpenRegister ? (
            <OpenSessionForm restaurantId={restaurant.id} />
          ) : (
            <div className="flex items-center justify-center py-12 text-center text-sm text-muted-foreground">
              Aucune session de caisse ouverte. Vous n'avez pas la permission
              d'ouvrir une session.
            </div>
          )}
        </div>
      </RoleProtectedPage>
    </DashLayout>
  );
};

export default RegisterPage;
